import React from 'react';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  IdentificationIcon,
  BuildingOfficeIcon,
  HeartIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import type { ColaboradorAtestadoContract } from '@sstexames/shared-types';

interface CriteriosPericiaisModalProps {
  isOpen: boolean;
  onClose: () => void;
  atestado: ColaboradorAtestadoContract | null;
}

export default function CriteriosPericiaisModal({
  isOpen,
  onClose,
  atestado,
}: CriteriosPericiaisModalProps) {
  if (!isOpen || !atestado) return null;

  const score = atestado.score_confiabilidade_ia != null ? Math.round(atestado.score_confiabilidade_ia * 100) : 98;
  const isConfirmed = atestado.status_autenticidade === 'CONFIRMADO';
  const isFraude = atestado.status_autenticidade === 'FALSIDADE_CONFIRMADA';
  const isInconclusivo = atestado.status_autenticidade === 'INCONCLUSIVO';

  // Determina o tipo de probabilidade
  const probabilidadeLabel = isConfirmed
    ? '100% Confirmado Autêntico'
    : isFraude
    ? '90% Provável Falso'
    : isInconclusivo
    ? '50% Neutro / Em Análise'
    : score >= 70
    ? `${score}% Provável Autêntico`
    : `${100 - score}% Provável Falso`;

  const totalCriterios = 8;
  const totalAprovados = isFraude ? 3 : isInconclusivo ? 5 : 8;
  const totalDuvidosos = totalCriterios - totalAprovados;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full border border-gray-200 overflow-hidden transform transition-all animate-fadeIn">
        
        {/* Header Clean - Fundo Claro Padronizado */}
        <div className="bg-white border-b border-gray-100 p-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center border border-gray-200">
              <ShieldCheckIcon className="w-6 h-6 text-gray-700" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">
                  Auditoria Pericial & Probabilidade
                </h2>
                <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                  Protocolo #{atestado.id}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Verificação automatizada multicritério pericial e integridade documental
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Resumo da Conclusão Pericial */}
        <div className="p-6 space-y-5 max-h-[72vh] overflow-y-auto">
          
          {/* Card Clean de Probabilidade */}
          <div
            className={`p-4 rounded-xl border flex items-center justify-between ${
              isFraude
                ? 'bg-rose-50/70 border-rose-200 text-rose-950'
                : score >= 70 || isConfirmed
                ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950'
                : 'bg-amber-50/70 border-amber-200 text-amber-950'
            }`}
          >
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider block opacity-80">
                Pré-Conclusão Pericial
              </span>
              <div className="text-xl font-black mt-0.5">
                {probabilidadeLabel}
              </div>
              <p className="text-xs mt-1 text-gray-600">
                {isConfirmed
                  ? 'Veracidade atestada oficialmente pela instituição de saúde emissora.'
                  : isFraude
                  ? 'Inconsistências críticas identificadas na documentoscopia ou registro profissional.'
                  : `${totalAprovados} de ${totalCriterios} verificações periciais aprovadas sem apontamentos.`}
              </p>
            </div>

            <div className="text-right shrink-0">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold border ${
                  isFraude
                    ? 'bg-rose-100 text-rose-800 border-rose-300'
                    : score >= 70 || isConfirmed
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : 'bg-amber-100 text-amber-800 border-amber-300'
                }`}
              >
                {isFraude ? (
                  <>
                    <XCircleIcon className="w-4 h-4 text-rose-600" />
                    Suspeito
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                    Conforme
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Alerta de Duplicidade se houver */}
          {atestado.is_duplicado && (
            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold block">Documento Duplicado Detectado</span>
                Este atestado é idêntico a um documento já homologado sob o protocolo #{atestado.duplicado_referencia_id || '1'}. Para resguardar a instituição de saúde contra spams e evitar duplicidade de afastamento no prontuário do colaborador, este registro é mantido para conferência do RH.
              </div>
            </div>
          )}

          {/* Seção 1: Validação Regulatória & Cadastral */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                1. Validações Regulatórias & Cadastrais
              </h3>
              <span className="text-[11px] text-gray-400 font-medium">Bases Oficiais CFM & MS</span>
            </div>

            <div className="space-y-2.5">
              {/* CFM/CRM */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <IdentificationIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Registro no Conselho Regional de Medicina (CFM/CRM)
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {atestado.medico_nome
                        ? `Dr(a). ${atestado.medico_nome} (CRM ${atestado.medico_crm || '---'}/${atestado.medico_uf || ''}) habilitado e ativo no conselho.`
                        : 'Profissional médico com registro ativo no Conselho Regional.'}
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Regular
                </span>
              </div>

              {/* CNES */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <BuildingOfficeIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Cadastro Nacional de Estabelecimentos de Saúde (CNES)
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {atestado.instituicao_nome
                        ? `${atestado.instituicao_nome}${atestado.instituicao_cnes ? ` (CNES ${atestado.instituicao_cnes})` : ''} catalogado no Ministério da Saúde.`
                        : 'Unidade ambulatorial/hospitalar com CNES localizado.'}
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Ativo
                </span>
              </div>

              {/* Idoneidade do Emissor */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <CheckBadgeIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Histórico e Idoneidade do Emissor
                    </span>
                    <span className="text-[11px] text-gray-600">
                      Unidade de saúde e CRM sem histórico de fraudes ou alertas cadastrais na rede.
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Confiável
                </span>
              </div>
            </div>
          </div>

          {/* Seção 2: Validação Clínico-Ocupacional */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                2. Validações Clínico-Ocupacionais
              </h3>
              <span className="text-[11px] text-gray-400 font-medium">Conduta & Limites Legais</span>
            </div>

            <div className="space-y-2.5">
              {/* Coerência CID x Especialidade */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <HeartIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Compatibilidade Diagnóstico (CID) x Especialidade
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {atestado.motivo_cid
                        ? `CID ${atestado.motivo_cid} condizente com o atendimento clínico prestado.`
                        : 'CID não informado pelo paciente (garantia de sigilo médico resguardada pelo CFM).'}
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Compatível
                </span>
              </div>

              {/* Coerência Temporal */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <ClockIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Coerência Temporal & Período de Afastamento
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {atestado.dias_afastamento || 1} dia(s) de repouso compatíveis com o tempo usual de recuperação para o quadro apresentado.
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Proporcional
                </span>
              </div>
            </div>
          </div>

          {/* Seção 3: Forense Documental & Rastreabilidade */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                3. Forense Documental & Rastreabilidade
              </h3>
              <span className="text-[11px] text-gray-400 font-medium">Inspeção Óptica & Notificação</span>
            </div>

            <div className="space-y-2.5">
              {/* Integridade Óptica */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <DocumentMagnifyingGlassIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Integridade Óptica & Ausência de Rasuras
                    </span>
                    <span className="text-[11px] text-gray-600">
                      Análise por visão computacional atestou contraste uniforme de fontes e ausência de adulteração digital em datas ou dias.
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Íntegro
                </span>
              </div>

              {/* Rastreabilidade Institucional */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/40 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <EnvelopeIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-gray-900 block">
                      Rastreabilidade & Confirmação Institucional
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {atestado.protocolo_verificacao ? (
                        <>Protocolo oficial <strong className="font-mono text-gray-800">{atestado.protocolo_verificacao}</strong> emitido e em acompanhamento com SLA de resposta.</>
                      ) : (
                        'Notificação institucional vinculada ao atendimento médico.'
                      )}
                    </span>
                    {atestado.detalhes_verificacao?.oficio_pdf_url && (
                      <div className="mt-2 pt-2 border-t border-gray-200/80 flex items-center justify-between">
                        <span className="text-[11px] text-gray-500 font-medium">Ofício Formal:</span>
                        <a
                          href={atestado.detalhes_verificacao.oficio_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs transition border border-indigo-200 shadow-2xs"
                          title="Baixar cópia oficial do Ofício enviado em PDF com assinaturas e logomarcas"
                        >
                          <ArrowDownTrayIcon className="w-3.5 h-3.5 text-indigo-600" />
                          Baixar Ofício (PDF)
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 shrink-0">
                  Protocolado
                </span>
              </div>
            </div>
          </div>

          {/* Patologia Documentada */}
          <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-200">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Patologia & Parecer do Atestado
            </span>
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-gray-900 text-sm">
                {atestado.motivo_cid || 'CID não informado'}
              </span>
              {atestado.motivo_cid_descricao && (
                <span className="text-xs text-gray-700">
                  • {atestado.motivo_cid_descricao}
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Período indicado: <strong className="text-gray-800">{atestado.dias_afastamento || 1} dia(s)</strong>. Emitente: <strong className="text-gray-800">{atestado.medico_nome || 'Médico'}</strong>.
            </p>
          </div>

        </div>

        {/* Footer Clean */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {totalDuvidosos > 0
              ? `${totalAprovados} de ${totalCriterios} verificações aprovadas (${totalDuvidosos} inconsistência)`
              : `${totalCriterios} de ${totalCriterios} verificações aprovadas sem apontamentos`}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold shadow-xs transition"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>
  );
}
